import type {
  Need,
  Skill,
  SkillCategory,
  SkillNeedTemplate
} from '@lmc/ts-common'

import { InputSelect, InputText, InputToggle, Modal } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import {
  type NeedCategoryCode,
  NeedCategoryDescriptionEnum
} from '@lmc/validations'
import { Divider, Form, Input } from 'antd'
import type * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import styled from 'styled-components'
import useFeatureFlags from '../../../../hooks/useFeatureFlags'
import { fetchAllNeeds, NEEDS_QUERY_KEYS } from '../../../../hooks/useNeeds'
import {
  useSkillTemplateCreate,
  useSkillTemplateUpdate
} from '../../../../hooks/useSkillTemplates'

const UNCATEGORIZED = 'Uncategorized'

type SkillNeedTemplateModalProps = {
  isModalVisible: boolean
  handleToggleModal: () => void
  categoriesOptions?: SkillCategory[]
  needOptions?: Need[]
  skillOptions?: Skill[]
  skillNeedTemplate?: SkillNeedTemplate

  onRefetch?: () => void
  createdNeedsIds: string[]
  createdSkillIds: string[]
}

export const SkillNeedTemplateModal = ({
  handleToggleModal,
  isModalVisible,
  categoriesOptions,
  needOptions,
  skillOptions,
  skillNeedTemplate,
  onRefetch,
  createdNeedsIds,
  createdSkillIds
}: SkillNeedTemplateModalProps): React.ReactElement => {
  const { t } = useTranslation()

  const flags = useFeatureFlags({ flagNames: ['mods_needs_settings'] })?.data
  const isNeedsCarePlanEnabled = flags?.mods_needs_settings?.enabled ?? false

  const { data: allNeeds } = useQuery(
    [NEEDS_QUERY_KEYS.options],
    fetchAllNeeds,
    {
      retry: false,
      retryOnMount: false,
      enabled: isNeedsCarePlanEnabled
    }
  )

  const groupedNeedOptions = useMemo(() => {
    if (!isNeedsCarePlanEnabled) return undefined

    const v6Needs = allNeeds ?? []
    const v6Ids = new Set(v6Needs.map((n) => n.id))

    // Legacy needs that are already selected in this template — only for label resolution
    const selectedLegacyNeeds = ((skillNeedTemplate?.needs as string[]) ?? [])
      .filter((id) => !v6Ids.has(id))
      .map((id) => (needOptions ?? []).find((n) => n.id === id))
      .filter(Boolean) as Need[]

    const allNeedsForOptions = [...v6Needs, ...selectedLegacyNeeds] as Need[]
    const groups: Record<
      string,
      { label: string; value: string; disabled?: boolean }[]
    > = {}

    allNeedsForOptions.forEach((need) => {
      const categoryCode = (need as any).modsCategory as
        | NeedCategoryCode
        | undefined
      const groupLabel = categoryCode
        ? (NeedCategoryDescriptionEnum[categoryCode] ?? categoryCode)
        : UNCATEGORIZED

      if (!groups[groupLabel]) {
        groups[groupLabel] = []
      }

      groups[groupLabel].push({
        label: need?.name || 'N/A',
        value: need?.id,
        disabled: createdNeedsIds
          ?.filter((item) => skillNeedTemplate?.needs?.[0] !== item)
          .includes(need.id)
      })
    })

    return Object.entries(groups)
      .sort(([a], [b]) => {
        if (a === UNCATEGORIZED) return 1
        if (b === UNCATEGORIZED) return -1
        return a.localeCompare(b)
      })
      .map(([label, options]) => ({ label, options }))
  }, [
    isNeedsCarePlanEnabled,
    allNeeds,
    needOptions,
    createdNeedsIds,
    skillNeedTemplate
  ])

  const legacyNeedOptions = useMemo(() => {
    if (isNeedsCarePlanEnabled) return undefined

    return (
      needOptions?.map((need) => ({
        label: need?.name || 'N/A',
        value: need?.id,
        disabled: createdNeedsIds
          ?.filter((item) => skillNeedTemplate?.needs?.[0] !== item)
          .includes(need.id)
      })) || []
    )
  }, [isNeedsCarePlanEnabled, needOptions, createdNeedsIds, skillNeedTemplate])

  const [form] = Form.useForm()

  const handleFinish = () => {
    onRefetch?.()
    handleCloseForm()
  }

  const { createSkillTemplate, isCreating } =
    useSkillTemplateCreate(handleFinish)
  const { updateSkillTemplate, isUpdating } =
    useSkillTemplateUpdate(handleFinish)
  const isLoading = isCreating || isUpdating

  const onConfirm = () => {
    form.submit()
  }

  const [formChange, setFormChange] = useState<SkillNeedTemplate | undefined>(
    skillNeedTemplate ?? ({ trainingRequired: false } as SkillNeedTemplate)
  )

  const [trainingValidForEnabled, setTrainingValidForEnabled] = useState(
    skillNeedTemplate?.trainingValidFor != null &&
      skillNeedTemplate?.trainingValidFor > 0
  )

  const handleCloseForm = () => {
    handleToggleModal()
    handleCleanForm()
  }

  const handleCleanForm = () => {
    form.resetFields()
    form.setFieldsValue({})
    setFormChange({ trainingRequired: false } as SkillNeedTemplate)
    setTrainingValidForEnabled(false)
  }

  const skillNeedTemplateFormValues = {
    ...skillNeedTemplate,
    trainingRequired: skillNeedTemplate?.trainingRequired,
    trainingValidFor:
      skillNeedTemplate?.trainingValidFor === 0
        ? undefined
        : skillNeedTemplate?.trainingValidFor,
    ...(!skillNeedTemplate?.trainingRequired && { trainingName: undefined })
  }

  useEffect(() => {
    if (skillNeedTemplate) {
      form.setFieldsValue(skillNeedTemplateFormValues)
      setFormChange(skillNeedTemplateFormValues as SkillNeedTemplate)
      setTrainingValidForEnabled(
        skillNeedTemplate?.trainingValidFor != null &&
          skillNeedTemplate?.trainingValidFor > 0
      )
    } else if (formChange) {
      handleCleanForm()
    }
  }, [skillNeedTemplate])

  // Re-set needs form value when options load to fix label display
  useEffect(() => {
    if (skillNeedTemplate?.needs && (allNeeds || needOptions)) {
      form.setFieldsValue({ needs: skillNeedTemplate.needs })
    }
  }, [allNeeds, needOptions])

  const onFinish = async ({
    categories,
    needs,
    skill,
    ...values
  }: {
    categories: string[]
    trainingRequired: boolean
    needs: string[]
    skill: string | string[]
  }) => {
    // InputToggle in controlled mode (checked prop) doesn't register with Form,
    // so trainingRequired won't appear in form values — take it from formChange state
    const trainingRequired = formChange?.trainingRequired ?? false
    const { trainingName, trainingValidFor, ...restValues } = values as any

    // When editing, skill is displayed as read-only text (no form field),
    // so fall back to the existing skillNeedTemplate value
    const resolvedSkill = skill ?? skillNeedTemplate?.skill

    const payload = {
      ...restValues,
      trainingRequired,
      ...(trainingRequired
        ? { trainingName, trainingValidFor }
        : { trainingName: null, trainingValidFor: null }),
      ...((!!categories?.length || !!skillNeedTemplate?.categories?.length) && {
        categories
      }),
      ...((!!needs?.length || !!skillNeedTemplate?.needs?.length) && {
        needs
      }),
      skill: Array.isArray(resolvedSkill) ? resolvedSkill[0] : resolvedSkill
    } as unknown as SkillNeedTemplate

    try {
      if (skillNeedTemplate?.id) {
        await updateSkillTemplate({ ...payload, id: skillNeedTemplate.id })
      } else {
        await createSkillTemplate(payload)
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
      const err = e as Record<string, unknown>
      if (typeof e === 'object' && e !== null && err.code === 1001) {
        form.setFields([
          {
            name: 'needs',
            errors: [t('skillsAndNeeds.form.needAlreadyUsed')]
          }
        ])
      }
    }
  }

  return (
    <StyledModal
      localisation={{
        title: t(
          `skillsAndNeeds.${skillNeedTemplate ? 'editSkillModalTitle' : 'addSkillModalTitle'}`
        ),
        confirmText: t(`general.buttons.confirm`),
        cancelText: t(`general.buttons.cancel`)
      }}
      onConfirm={onConfirm}
      onCancel={handleCloseForm}
      isModalVisible={isModalVisible}
      closeButton
      closeOnConfirm={false}
      noPadding
      confirmButtonProps={{ isLoading, variant: 'primary' }}
    >
      <Divider style={{ margin: '0 0 16px 0' }} />
      <Form
        form={form}
        onFinish={onFinish}
        initialValues={{ trainingRequired: false }}
        onValuesChange={(e) => setFormChange({ ...formChange, ...e })}
      >
        {skillNeedTemplate ? (
          <StyledReadOnlyField>
            <StyledFieldLabel weight="medium">
              {t('skillsAndNeeds.form.skillName')}
            </StyledFieldLabel>
            <StyledFieldValue>
              {skillOptions?.find((s) => s.id === skillNeedTemplate.skill)
                ?.name || skillNeedTemplate.skill}
            </StyledFieldValue>
          </StyledReadOnlyField>
        ) : (
          <InputSelect
            testId="skill-name-select"
            className="skill-name-item"
            errorMessage={t('skillsAndNeeds.form.skillNameRequired')}
            type="skill"
            required
            label={t('skillsAndNeeds.form.skillName')}
            helperText={t('skillsAndNeeds.form.skillNameTooltip')}
            layout="column"
            placeholder={t('skillsAndNeeds.form.selectFromList')}
            options={
              skillOptions?.map((skill) => ({
                label: skill?.name || 'N/A',
                value: skill?.id,
                disabled: createdSkillIds?.includes(skill.id)
              })) || []
            }
            showSearch
            onChange={(e) => {
              const selected = e?.slice(-1)
              const selectedId = selected?.[0]
              form.setFields([{ name: 'skill', value: selected }])
              if (selectedId && createdSkillIds.includes(selectedId)) {
                setTimeout(() => {
                  form.setFields([
                    {
                      name: 'skill',
                      errors: [t('skillsAndNeeds.form.skillAlreadyAdded')]
                    }
                  ])
                }, 0)
              }
            }}
            mode="tags"
          />
        )}
        <InputToggle
          type="trainingRequired"
          label={t('skillsAndNeeds.form.trainingRequired')}
          required
          checked={formChange?.trainingRequired}
          onChange={(checked) => {
            form.setFieldsValue({ trainingRequired: checked })
            if (!checked) {
              form.setFields([
                { name: 'trainingName', value: null, errors: [] },
                { name: 'trainingValidFor', value: null, errors: [] }
              ])
              setFormChange({
                ...formChange,
                trainingRequired: checked,
                trainingValidFor: null,
                trainingName: null
              } as unknown as SkillNeedTemplate)
            } else {
              setFormChange({
                ...formChange,
                trainingRequired: checked
              } as unknown as SkillNeedTemplate)
            }
          }}
        />
        {formChange?.trainingRequired && (
          <InputText
            type="trainingName"
            label={t('skillsAndNeeds.form.trainingName')}
            layout="column"
            placeholder={t('skillsAndNeeds.form.trainingNameRequired')}
            style={{ width: '100%', height: 40 }}
            required
            errorMessage={t('skillsAndNeeds.form.trainingNameIsRequired')}
            wrapperClassName="training-name-item"
          />
        )}
        {formChange?.trainingRequired && (
          <>
            <div style={{ marginBottom: trainingValidForEnabled ? 0 : 4 }}>
              <InputToggle
                label={t('skillsAndNeeds.form.trainingExpires')}
                required
                checked={trainingValidForEnabled}
                onChange={(checked) => {
                  setTrainingValidForEnabled(checked)
                  if (!checked) {
                    form.setFields([
                      { name: 'trainingValidFor', value: null, errors: [] }
                    ])
                    setFormChange({
                      ...formChange,
                      trainingValidFor: null
                    } as unknown as SkillNeedTemplate)
                  }
                }}
              />
            </div>
            {trainingValidForEnabled && (
              <Form.Item shouldUpdate noStyle>
                {({ getFieldError }) => {
                  const errors = getFieldError('trainingValidFor')
                  const hasError = errors.length > 0
                  return (
                    <>
                      <TrainingValidForRow hasError={hasError}>
                        <Form.Item
                          name="trainingValidFor"
                          label={t('skillsAndNeeds.form.trainingValidFor')}
                          rules={[
                            {
                              required: true,
                              message: t('skillsAndNeeds.form.validForRequired')
                            }
                          ]}
                          labelCol={{ span: 24 }}
                          wrapperCol={{ span: 24 }}
                          style={{ marginBottom: 0 }}
                          className="training-valid-for-item"
                        >
                          <Input
                            placeholder="0"
                            style={{ width: 150, height: 40 }}
                            onKeyDown={(e) => {
                              if (
                                !/[0-9]/.test(e.key) &&
                                ![
                                  'Backspace',
                                  'Delete',
                                  'ArrowLeft',
                                  'ArrowRight',
                                  'Tab'
                                ].includes(e.key)
                              ) {
                                e.preventDefault()
                              }
                            }}
                          />
                        </Form.Item>
                        <TrainingValidForUnit>
                          {t('skillsAndNeeds.form.months')}
                        </TrainingValidForUnit>
                      </TrainingValidForRow>
                      {hasError && (
                        <TrainingValidForError>
                          {errors[0]}
                        </TrainingValidForError>
                      )}
                    </>
                  )
                }}
              </Form.Item>
            )}
          </>
        )}
        <InputSelect
          testId="covered-needs-select"
          showSearch
          type="needs"
          label={t('skillsAndNeeds.form.coveredNeeds')}
          layout="column"
          placeholder={t('skillsAndNeeds.form.coveredNeedsPlaceholder')}
          {...(isNeedsCarePlanEnabled
            ? { groupedOptions: groupedNeedOptions, mode: 'multiple' as const }
            : { options: legacyNeedOptions, mode: 'tags' as const })}
          helperText={t('skillsAndNeeds.form.coveredNeedsTooltip')}
        />
        <InputSelect
          testId="category-select"
          type="categories"
          mode="tags"
          showSearch
          label={t('skillsAndNeeds.form.category')}
          layout="column"
          placeholder={t('skillsAndNeeds.form.startTyping')}
          options={
            categoriesOptions?.map((category) => ({
              label: category.name || 'N/A',
              value: category.id
            })) || []
          }
          helperTextElement={
            <>
              {t('skillsAndNeeds.form.categoryHelperText1')}
              <a
                href="/reports/training-matrix"
                style={{ fontWeight: 500, textDecoration: 'underline' }}
              >
                {t('skillsAndNeeds.form.categoryHelperLink')}
              </a>
              {t('skillsAndNeeds.form.categoryHelperText2')}
            </>
          }
          onChange={(e) => {
            form.setFields([{ name: 'categories', value: e }])
          }}
        />
      </Form>
      <Divider style={{ margin: '8px 0 0 0' }} />
    </StyledModal>
  )
}

const StyledModal = styled(Modal)`
  min-width: 600px;

  .ant-modal-content {
    padding-top: 20px !important;
    padding-left: 24px !important;
    padding-right: 24px !important;
  }

  .ant-form-item-label > label {
    font-weight: 500 !important;
    font-size: 15px !important;
  }

  .ant-form-item-label > label span {
    font-size: 15px !important;
  }

  .ant-form-item-explain-error {
    font-size: 15px !important;
  }

  .ant-form-item-control {
    position: relative;
  }

  .ant-form-item-explain {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    margin-top: 4px;
  }

  .skill-name-item .ant-form-item-control,
  .training-name-item .ant-form-item-control {
    margin-bottom: 12px !important;
  }

  .skill-name-item.ant-form-item-has-error .ant-form-item-control,
  .training-name-item.ant-form-item-has-error .ant-form-item-control {
    margin-bottom: 24px !important;
  }

  .ant-select {
    min-height: 40px !important;
    height: auto !important;
  }

  .ant-select-selector {
    min-height: 40px !important;
    height: auto !important;
  }

  .ant-input::placeholder {
    font-size: 14px !important;
  }

  .ant-modal-header {
    padding-bottom: 4px !important;
  }

  .ant-modal-title {
    font-size: 22px !important;
    font-weight: 500 !important;
    text-transform: none !important;
    color: ${({ theme }) => theme.color.neutral.color3} !important;
  }

  .ant-modal-footer {
    margin-top: 0 !important;
    padding-top: 12px !important;
    padding-bottom: 12px !important;
  }
`

const StyledReadOnlyField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
`

const StyledFieldLabel = styled(Text)`
  font-weight: 500 !important;
  font-size: 15px !important;
`

const StyledFieldValue = styled(Text)`
  font-weight: 400 !important;
  font-size: 15px !important;
  color: ${({ theme }) => theme.color.neutral.color1};
`

const TrainingValidForRow = styled.div<{ hasError?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: ${({ hasError }) => (hasError ? 0 : 12)}px;

  .training-valid-for-item {
    flex: none;
    width: 150px;
    margin-bottom: 0 !important;
  }

  .training-valid-for-item .ant-form-item-explain {
    display: none;
  }

  .training-valid-for-item .ant-form-item-row {
    flex-direction: column;
  }

  .training-valid-for-item .ant-col {
    flex: none !important;
    max-width: none !important;
    width: auto !important;
  }

  .training-valid-for-item .ant-form-item-control {
    margin-bottom: 0 !important;
  }
`

const TrainingValidForError = styled.div`
  font-size: 15px !important;
  color: #ff4d4f;
  margin-top: 4px;
`

const TrainingValidForUnit = styled.span`
  font-size: 15px;
  color: ${({ theme }) => theme.color.neutral.color3};
  align-self: flex-start;
  margin-top: 28px;
  line-height: 40px;
`

export default SkillNeedTemplateModal
